<template>
    <div>
        <table-page
            v-show="!addingLocation"
            :client="client"
            @updated="getLocations"
            :warnings="warnings"
            :changelogs="changelogs"
            :hideTabs="true"
        >
            <fast-table
                :recordLabel="recordLabel"
                :sections="sections"
                :headers="headers"
                :data="tableData"
                :activeRowId="currentLocationId"
                @click="currentLocationId = $event.id"
            >
                <template v-slot:table-actions>
                    <button class="btn btn-primary btn-collapse" id="addLocationButton" @click="addLocation">
                        <i class="far fa-fw fa-plus"></i> <span class="btn-text">Add Location</span>
                    </button>
                </template>
            </fast-table>

        </table-page>

        <div v-if="addingLocation" class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">
                    <h3>Add Location</h3>
                    <template v-if="addLocationStep == 'company'">
                        <p class="mb-hf">Which company does this location belong to?</p>
                        <company-selector
                            :client="client"
                            :companies="client.companies"
                            v-model="formData.companies"
                            :radio="true"
                        />
                    </template>
                    <template v-else-if="addLocationStep == 'locations'">
                        <p>Enter the information for this new location</p>
                        <div class="row" v-for="(location, idx) in formData.locations">
                            <div class="col-12">
                                <div class="card mb-2">
                                    <div class="card-header pr-1 d-flex align-items-center">
                                        <span class="btnlike mr-auto">
                                            <h4 class="mb-0">
                                                {{ selectedCompany.name }} - Location {{ idx + 1 }}
                                            </h4>
                                        </span>
                                    </div>
                                    <div class="card-body">
                                        <form>
                                            <form-input label="Location Name" v-model="location.name" :errors="errors.name" type="text" :maxlength="100"/>
                                            <div class="form-row">
                                                <div class="col-6">
                                                    <form-input label="POS ID (Optional)" v-model="location.external_pos_id" :errors="errors.external_pos_id" type="text" :maxlength="20"/>
                                                </div>
                                                <div class="col-6">
                                                    <form-input label="Phone Number (Optional)" v-model="location.phone_number" type="phone" :errors="errors.phone_number" />
                                                </div>
                                            </div>
                                            <form-input label="Address 1" v-model="location.address_1" :errors="errors.address_1" type="text" :maxlength="100"/>
                                            <form-input label="Address 2 (Optional)" v-model="location.address_2" :errors="errors.address_2" type="text" :maxlength="100"/>
                                            <form-input label="City" v-model="location.city" :errors="errors.city" type="text" :maxlength="100"/>

                                            <div class="form-row">
                                                <div class="col-6">
                                                    <form-input label="State" v-model="location.state" :errors="errors.state" type="select" :options="stateOptions"/>
                                                </div>

                                                <div class="col-6">
                                                    <form-input label="Zip Code" :maxlength="5" v-model="location.postal_code" :errors="errors.postal_code" type="text" inputmode="tel" />
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="row justify-content-center">
                            <a href class="btn btn-link mb-2" @click.prevent="addLocationFormData()"><i class="far fa-plus mr-hf"></i>Add Another Location</a>
                        </div>
                    </template>

                    <form>
                        <form-errors :errors="formErrors"/>

                        <div class="row mb-2 d-flex align-items-center">
                            <div class="col">
                                <button v-if="addLocationStep == 'company'" class="btn-outline-primary btn" @click.prevent="addingLocation = false; formData = makeFormData()">Back</button>
                                <button v-else-if="addLocationStep == 'locations'" class="btn-outline-primary btn" @click.prevent="addLocationStep = 'company'">Back</button>
                            </div>

                            <div class="col text-right">
                                <button v-if="addLocationStep == 'company'" class="btn-primary btn" @click.prevent="addLocationStep = 'locations'">Continue</button>
                                <button v-else class="btn btn-primary" @click.prevent="onSubmit">Save</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <location-sidebar
            v-if="currentLocation"
            :client="client"
            :location-id="currentLocation.id"
            ref="locationSidebar"
            @updated="getLocations"
            @close="currentLocationId = null"
        >
        </location-sidebar>
    </div>
</template>

<script>
import TablePageMixin from '@/mixins/TablePageMixin'
import FastTable from '@/components/FastTable'
import LocationSidebar from './components/Locations/LocationSidebar'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import STATES from '@/states'

export default {
    components: {FastTable, LocationSidebar, CompanySelector},
    mixins: [TablePageMixin],
    props: ['client'],
    computed: {
        currentLocation() {
            return this.locations.find(e => (e.id == this.currentLocationId))
        },
        sections() {
            let sections = [
                {title: 'Active', defaultIsClosed: false, id: 'active', hasDateFilter: false},
                {title: 'Inactive', defaultIsClosed: true, id: 'inactive', hasDateFilter: true},
            ]
            return sections
        },
        activeLocations() {
            return this.locations.filter((p) => {return p.is_active})
        },
        inactiveLocations() {
            return this.locations.filter((p) => {return !p.is_active})
        },
        tableData() {
            const tableData = []
            const subsets = [
                this.activeLocations,
                this.inactiveLocations,
            ]

            subsets.forEach(locations => {
                const sectionData = []

                locations.forEach(location => {
                    sectionData.push({
                        id: location.id,
                        object: location,
                        cells: this.makeLocationTableData(location),
                    })
                })
                tableData.push(sectionData)
            })
            return tableData
        },
        selectedCompany() {
            return this.client.companies.find((c) => {return c.id == this.formData.companies[0]})
        },
        stateOptions() {
            const opts = []
            STATES.forEach(s => {
                opts.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return opts
        },
    },
    data() {
        return {
            locations: [],
            addingLocation: false,
            addLocationStep: 'company',
            dataLoaded: false,
            currentLocationId: null,
            recordLabel: "Locations",
            headers: [
                {label: 'Location Name', classes: 'cw-12', isSearchable: true, defaultSort: true},
                {label: 'Company', classes: 'cell-company', isFilterable: true, isSearchable: true, defaultSort: false},
                {label: 'Warnings', classes: 'cw-warning', isSearchable: false, isFilterable: true},
                {label: 'POS ID', classes: 'cell-right', isFilterable: false, isSearchable: false, defaultSort: false},
                {label: 'Employees', classes: 'cell-right', isFilterable: false, isSearchable: false, defaultSort: false},
            ],
            formData: this.makeFormData(),
            errors: [],
            formErrors: [],
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Locations')
        this.getLocations()
    },
    methods: {
        getLocations() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/locations`).then(resp => {
                this.locations = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                }
                else {
                    this.$bus.showError(errors.__all__)
                }
            })
        },
        makeLocationTableData(location) {
            let companyName = this.client.companies.find(c => c.id == location.company_id).name
            return [
                location.name,
                companyName,
                '', // warnings
                location.external_pos_id,
                location.num_employees,
            ]
        },
        addLocation() {
            this.addingLocation = true
        },
        makeLocationFormData() {
            return {
                    name: '',
                    address_1: '',
                    address_2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    external_pos_id: '',
                }
        },
        makeFormData() {
            return {
                company: null,
                companies: [],
                locations: [this.makeLocationFormData()]
            }
        },
        addLocationFormData() {
            this.formData.locations.push(this.makeLocationFormData())
        },
        getFormData() {
            this.formData.company = this.formData.companies[0]
            return this.formData
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/settings/locations`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.locations = resp
                this.addingLocation = false
                this.formData = this.makeFormData()
                this.addLocationStep = 'company'
                this.$emit('updated')

            }).catch((errors) => {
                console.log(errors)
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    },
}
</script>
